import { accent, meta, node, rule } from "./ansi";

// Real scrollback for the inspector figure: ten units of rwc-01 session
// 14925 — the same session the figure's own excerpt comes from — rendered by
// tsh itself (tsh render 14925) and reduced to the conversational rows: the
// unit header, the prompt, and the opening of Codex's summary. Prompts and
// summaries are VERBATIM (wrapped to the pane, elided with an ellipsis);
// nothing is invented, and the unit ids and times are the session's own.
//
// The rows exist so the figure's thread has history to scroll through — the
// visible tail stays the curated excerpt in content.ts, and everything above
// it is where the virtual scroller earns its keep.

interface HistoryUnit {
  unit: number;
  time: string;
  prompt: string;
  summary: string;
}

const UNITS: HistoryUnit[] = [
  { unit: 274, time: "09:53",
    prompt: "works now but we shouldn't select the bottom newline",
    summary: "Good catch. The renderer item identity currently includes component-owned spacing, so the approval’s bottom padding inherits the selection band." },
  { unit: 282, time: "10:08",
    prompt: "let's improve the header to indicate the ctx % remaining (on the same line as `─── gpt-5.6-sol`). the gpt-5.6-sol should be prefixed by the auth if necessary. eg. @moo/fable",
    summary: "I’ll extend the existing Lisp-authored header footer rather than adding native chrome: derive the displayed model identity from the live agent snapshot, prepend its auth/profile only when present, and append context rema" },
  { unit: 283, time: "10:16",
    prompt: "let's add color to these",
    summary: "I’ll color the model identity and remaining-context value as separate footer spans, using the existing header/task-board palette so the rule stays subdued and the useful values carry emphasis.The footer currently accepts" },
  { unit: 285, time: "10:52",
    prompt: "let's add some slash commands for the codex lane. i want /compact",
    summary: "I’ll trace the Codex lane’s submit path and App Server protocol first." },
  { unit: 293, time: "13:06",
    prompt: "/compact",
    summary: "Context compacted" },
  { unit: 300, time: "13:08",
    prompt: "lets improve the slash commands for all lanes. / should open up a pane similar to autocomplete with all the available commands",
    summary: "I’ll trace the existing completion popup and lane command definitions, then make `/` a first-class command palette across lanes while keeping each lane’s command list accurate." },
  { unit: 302, time: "20:46",
    prompt: "/compact",
    summary: "Context compacted" },
  { unit: 310, time: "23:23",
    prompt: "let's add syntax coloring when we expand some code. consider how we can unify our entire syntax highlighting strategy",
    summary: "I’ll trace how expanded code blocks are represented and rendered, then identify the existing syntax-highlighting paths so we can route them through one shared strategy instead of adding a one-off colorizer." },
  { unit: 317, time: "05:16",
    prompt: "i'm expanding `    - // The verb stem inflects for all three tenses: `{stem}ing…` /",
    summary: "That example is an expanded patch, not a plain file-read payload." },
  { unit: 318, time: "07:25",
    prompt: "when i resize a pane (horizontally), it's very slow and seems to slowly tick before it finally reaches the final size",
    summary: "I’ll profile the horizontal-resize path and look for work repeated once per intermediate width—especially full transcript rerenders, component-catalog hashing, syntax parsing, and wrap/layout caches." },
];

const WIDTH = 44;

function wrapped(prefix: string, cont: string, text: string): string[] {
  const lines: string[] = [];
  let line = prefix;
  for (const word of text.split(/\s+/)) {
    const candidate = line === prefix || line === cont ? line + word : line + " " + word;
    if (candidate.length > WIDTH && line !== prefix && line !== cont) {
      lines.push(line);
      line = cont + word;
    } else {
      line = candidate;
    }
  }
  lines.push(line);
  return lines;
}

/** One unit as terminal rows, coloured by the page's roles. */
function $unit(entry: HistoryUnit): string[] {
  const header = `── unit ${entry.unit} · ${entry.time} `;
  const rows: string[] = [
    rule(header + "─".repeat(Math.max(0, WIDTH - header.length))),
    "",
  ];
  const [first, ...rest] = wrapped("› ", "  ", entry.prompt);
  rows.push(`${accent("›")} ${first.slice(2)}`, ...rest);
  rows.push("");
  const summary = wrapped("• ", "  ", entry.summary).slice(0, 3);
  const [s0, ...sn] = summary;
  rows.push(`${accent("•")} ${s0.slice(2)}`, ...sn.map((line) => line));
  if (wrapped("• ", "  ", entry.summary).length > 3) {
    rows[rows.length - 1] = rows[rows.length - 1].replace(/\s*$/, "") + meta("…");
  }
  rows.push("");
  return rows;
}

/** The codex pane's scrollback: ten real units, oldest first. */
export const INSPECTOR_CODEX_HISTORY: string[] = UNITS.flatMap($unit);

/** The thread pane's scrollback: the same units as selectable turn rows. */
export const INSPECTOR_THREAD_HISTORY: string[] = [
  ...UNITS.flatMap((entry) => [
    `  ${node(`turn:${entry.unit}`)}  ${meta(entry.time)}`,
  ]),
  "",
];
